import { memo, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Table, TableHeaderCell, TableRow } from '@spotify-internal/encore-web';
import { SortedByDirection } from '@spotify-internal/encore-web/types/src/core/components/TableHeaderCell/SortableTableHeaderCell';
import { useT } from '@spotify-internal/i18n-core';
import {
  AlbumSearchResult,
  SearchSortDirection,
  SearchSortField,
  StoredSearchResult,
} from '@hooks/useSearchedAlbums/types';
import { buildAddSnackbarAction } from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import { useTour } from '@context/TourContext';
import { SortProps } from '@components/Search/SearchResults/types';
import { TablePagination } from '@components/TablePagination/TablePagination';
import {
  DEFAULT_ROWS_PER_PAGE,
  SEARCH_RESULTS_PAGINATION_TOUR_COOKIE,
  SEARCH_SORT_DIRECTION_ASC,
  SEARCH_SORT_DIRECTION_DESC,
} from '@helpers/constants';
import { isEmptyString } from '@helpers/utils';
import { _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output } from '@generated/grpc/spotify/s4mpview/v1';
import { OnboardingPopover } from '../OnboardingPopover';
import { ResultsTableRow } from './ResultsTableRow';

const TableHeaderCellVerticalAlign = styled(TableHeaderCell)`
  vertical-align: middle;
`;

const SortableTableHeaderCell = styled(TableHeaderCellVerticalAlign)`
  button {
    overflow-wrap: break-word;
  }
`;

const ReleaseIdTableHeaderCell = styled(TableHeaderCell)`
  vertical-align: middle;
  padding-left: 28px;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const PaginationContainer = styled.div`
  position: relative;
`;

interface ResultsTableProps {
  results: AlbumSearchResult[] | StoredSearchResult[];
  userFeeds?: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[];
  userLicensors?: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[];
  showDeliveryStatusColumn?: boolean;
  gaEvents: {
    handleTableRowClick: () => void;
    handleClickCopyReleaseId: () => void;
    handleClickCopyURI: () => void;
    handleExpandTerritoriesClick: (prev: boolean) => void;
    handleLiveInTerritoriesApiRetry: () => void;
    handleSortClick?: (
      fieldName: SearchSortField,
      direction: 'ascending' | 'descending',
    ) => void;
    handlePaginationClick?: (actionType: 'increment' | 'decrement') => void;
    handleLimitChange?: (newValue: number) => void;
  };
  paginationProps?: {
    handlePagination: (updatedPage: number) => void;
    handleLimitChange: (updatedLimit: number) => void;
    resultLimit: string;
    currentPage: string;
  };
  sortProps?: SortProps;
}

const isFunction = (fn: Function | undefined): fn is Function =>
  typeof fn === 'function';

const searchSortToComponentSort = (
  columnSearchSortDirection: SearchSortDirection | 'none',
): SortedByDirection => {
  if (columnSearchSortDirection === SEARCH_SORT_DIRECTION_ASC) {
    return 'ascending';
  } else if (columnSearchSortDirection === SEARCH_SORT_DIRECTION_DESC) {
    return 'descending';
  }

  return 'none';
};

export const ResultsTable = memo(function ResultsTable({
  results,
  userFeeds,
  userLicensors,
  showDeliveryStatusColumn = false,
  gaEvents,
  paginationProps,
  sortProps,
}: ResultsTableProps) {
  const t = useT();
  const { handlePagination, handleLimitChange, resultLimit, currentPage } =
    paginationProps ?? {};
  const { handleSortChange, searchSortDirection, searchSortField } =
    sortProps ?? {};
  const [showedLastResultMessage, setShowedLastResultMessage] = useState(false);

  const [, dispatch] = useSnackbarNotifications();
  const {
    currentStep,
    tourCookieName,
    actions: { nextStep, endTour },
  } = useTour();

  const sortableColumnsDirectionState: {
    [key in SearchSortField]?: SearchSortDirection | null;
  } = {
    ALBUM_NAME:
      !!searchSortDirection && searchSortField === 'ALBUM_NAME'
        ? searchSortDirection
        : null,
    ORIGINAL_RELEASE_DATE:
      !!searchSortDirection && searchSortField === 'ORIGINAL_RELEASE_DATE'
        ? searchSortDirection
        : null,
  };

  const userHasMultipleFeeds = userFeeds && userFeeds.length > 1;
  const userHasMultipleLicensors = userLicensors && userLicensors.length > 1;
  const canPaginate =
    typeof currentPage === 'string' &&
    !isEmptyString(currentPage) &&
    isFunction(handlePagination);
  const page = Number(currentPage) || 1;
  const limit = Number(resultLimit) || DEFAULT_ROWS_PER_PAGE[0];
  const totalResultRows = results.length || 0;

  const onIncrement = () => {
    if (canPaginate) {
      handlePagination(Number(currentPage) + 1);

      if (isFunction(gaEvents.handlePaginationClick)) {
        gaEvents.handlePaginationClick('increment');
      }
    }
  };

  const onDecrement = useCallback(() => {
    if (canPaginate) {
      handlePagination(Math.max(1, Number(currentPage) - 1));

      if (isFunction(gaEvents.handlePaginationClick)) {
        gaEvents.handlePaginationClick('decrement');
      }
    }
  }, [currentPage, canPaginate, handlePagination, gaEvents]);

  const onLimitChange = (newValue: number) => {
    if (isFunction(handleLimitChange)) {
      handleLimitChange(newValue);
    }

    if (isFunction(gaEvents.handleLimitChange)) {
      gaEvents.handleLimitChange(newValue);
    }
  };

  const onSort = (fieldName: SearchSortField) => {
    let updatedSortDir: SearchSortDirection;
    const isDescending = searchSortDirection === SEARCH_SORT_DIRECTION_DESC;

    if (fieldName === searchSortField) {
      updatedSortDir = !isDescending
        ? SEARCH_SORT_DIRECTION_DESC
        : SEARCH_SORT_DIRECTION_ASC;
    } else {
      updatedSortDir = SEARCH_SORT_DIRECTION_DESC;
    }

    if (isFunction(handleSortChange)) {
      handleSortChange(fieldName, updatedSortDir);
    }

    if (isFunction(gaEvents.handleSortClick)) {
      gaEvents.handleSortClick(
        fieldName,
        updatedSortDir === SEARCH_SORT_DIRECTION_DESC
          ? 'descending'
          : 'ascending',
      );
    }
  };

  function getAriaLabel(fieldName: SearchSortField, columnName: string) {
    const oppositeDirection: SearchSortDirection =
      searchSortDirection === SEARCH_SORT_DIRECTION_ASC
        ? SEARCH_SORT_DIRECTION_DESC
        : SEARCH_SORT_DIRECTION_ASC;

    if (searchSortField !== fieldName) {
      return `Sort by ${columnName} in descending order.`;
    }

    return `Sort by ${columnName} in ${searchSortToComponentSort(
      oppositeDirection,
    )} order.`;
  }

  useEffect(() => {
    const showSnackbar = () => {
      dispatch(
        buildAddSnackbarAction(
          t(
            'atlas.snackbars.reached_end_of_search_results',
            "You've reached the end of the search results.",
            'Snackbar message to indicate the user has reached the end of the search results.',
          ),
        ),
      );
    };

    if (page > 1) {
      // the BE is not yet able to return the overall total # of search results
      // so for now we need to fetch the next page to know if we have more results to show
      // if no results, we can go back to the previous results page and let the user know
      if (results.length === 0) {
        onDecrement();

        if (!showedLastResultMessage) {
          showSnackbar();
          setShowedLastResultMessage(true);
        }
        return;
      }

      if (limit > totalResultRows && !showedLastResultMessage) {
        showSnackbar();
        setShowedLastResultMessage(true);
      }
    }
  }, [
    page,
    limit,
    totalResultRows,
    dispatch,
    t,
    results,
    onDecrement,
    setShowedLastResultMessage,
    showedLastResultMessage,
  ]);

  return (
    <>
      <Table>
        <colgroup>
          {userHasMultipleFeeds && <col width="10%" />}
          {userHasMultipleLicensors && <col width="10%" />}
          <col width="20%" />
          <col width="15%" />
          <col width="5%" />
          <col width="5%" />
          <col width="11%" />
          {showDeliveryStatusColumn && <col width="9%" />}
          <col width="10%" />
          <col width="4%" />
        </colgroup>
        <thead>
          <TableRow>
            {userHasMultipleFeeds && (
              <TableHeaderCellVerticalAlign>
                {t(
                  'atlas.labels.feed_name',
                  'Feed name',
                  'Label for Feed name',
                )}
              </TableHeaderCellVerticalAlign>
            )}
            {userHasMultipleLicensors && (
              <TableHeaderCellVerticalAlign>
                {t(
                  'atlas.labels.licensor_name',
                  'Licensor name',
                  'Label for Licensor name',
                )}
              </TableHeaderCellVerticalAlign>
            )}
            {isFunction(handleSortChange) ? (
              <SortableTableHeaderCell
                sortable
                sortedByDirection={
                  sortableColumnsDirectionState.ALBUM_NAME !== null
                    ? searchSortToComponentSort(
                        sortableColumnsDirectionState.ALBUM_NAME as SearchSortDirection,
                      )
                    : 'none'
                }
                iconPlacement="trailing"
                onClick={() => {
                  onSort('ALBUM_NAME');
                }}
                aria-label={getAriaLabel(
                  'ALBUM_NAME',
                  t(
                    'atlas.labels.product_details',
                    'Product details',
                    'Label for Product details',
                  ),
                )}
              >
                {t(
                  'atlas.labels.product_details',
                  'Product details',
                  'Label for Product details',
                )}
              </SortableTableHeaderCell>
            ) : (
              <TableHeaderCellVerticalAlign>
                {t(
                  'atlas.labels.product_details',
                  'Product details',
                  'Label for Product details',
                )}
              </TableHeaderCellVerticalAlign>
            )}
            <ReleaseIdTableHeaderCell>
              <div>
                {t(
                  'atlas.labels.primary_release_id',
                  'Primary release ID',
                  'Label for Primary release ID',
                )}
              </div>
            </ReleaseIdTableHeaderCell>
            <TableHeaderCellVerticalAlign>
              {t(
                'atlas.labels.product_type',
                'Product type',
                'Label for Product type',
              )}
            </TableHeaderCellVerticalAlign>
            <TableHeaderCellVerticalAlign>
              {t(
                'atlas.labels.included_media',
                'Included media',
                'Text for Included media label',
              )}
            </TableHeaderCellVerticalAlign>

            {isFunction(handleSortChange) ? (
              <SortableTableHeaderCell
                sortable
                sortedByDirection={
                  sortableColumnsDirectionState.ORIGINAL_RELEASE_DATE !== null
                    ? searchSortToComponentSort(
                        sortableColumnsDirectionState.ORIGINAL_RELEASE_DATE as SearchSortDirection,
                      )
                    : 'none'
                }
                iconPlacement="trailing"
                onClick={() => {
                  onSort('ORIGINAL_RELEASE_DATE');
                }}
                aria-label={getAriaLabel(
                  'ORIGINAL_RELEASE_DATE',
                  t(
                    'atlas.labels.original_release_date',
                    'Original release date',
                    'Label for Original release date',
                  ),
                )}
              >
                {t(
                  'atlas.labels.original_release_date',
                  'Original release date',
                  'Label for Original release date',
                )}
              </SortableTableHeaderCell>
            ) : (
              <TableHeaderCellVerticalAlign>
                {t(
                  'atlas.labels.original_release_date',
                  'Original release date',
                  'Label for Original release date',
                )}
              </TableHeaderCellVerticalAlign>
            )}

            {showDeliveryStatusColumn && (
              <TableHeaderCellVerticalAlign>
                {t(
                  'atlas.labels.delivery_status',
                  'Delivery status',
                  `Label for Delivery status. Options are 'Succeeded', 'Pending', 'Failed', and 'Unknown' `,
                )}
              </TableHeaderCellVerticalAlign>
            )}
            <TableHeaderCellVerticalAlign>
              <Flex>
                <div>
                  {t(
                    'atlas.labels.territories',
                    'Territories',
                    'Label for Territories',
                  )}
                </div>
              </Flex>
            </TableHeaderCellVerticalAlign>
            <TableHeaderCellVerticalAlign>
              {t(
                'atlas.actions.copy_uri',
                'Copy URI',
                'Text for Copy URI action',
              )}
            </TableHeaderCellVerticalAlign>
          </TableRow>
        </thead>
        <tbody>
          {!!results.length &&
            results.map((result, index) => (
              <ResultsTableRow
                key={`tablerow-${index}`}
                result={result}
                showFeedNameColumn={userHasMultipleFeeds}
                showLicensorNameColumn={userHasMultipleLicensors}
                showDeliveryStatusColumn={showDeliveryStatusColumn}
                gaEvents={gaEvents}
              />
            ))}
        </tbody>
      </Table>
      {!!paginationProps && (
        <PaginationContainer>
          <TablePagination
            pagination={{
              totalResultRows,
              page,
              onIncrement,
              onDecrement,
            }}
            limit={limit}
            rowsPerPage={{
              items: DEFAULT_ROWS_PER_PAGE,
              onValueChange: newValue => {
                onLimitChange(newValue);
              },
              customText: t(
                'atlas.table_pagination.results_per_page',
                'Results per page:',
                'Label for dropdown to select how many results per page to show',
              ),
            }}
          />
          {tourCookieName === SEARCH_RESULTS_PAGINATION_TOUR_COOKIE &&
            currentStep === 2 && (
              <OnboardingPopover
                onComplete={() => endTour()}
                onNextClick={() => nextStep()}
                onEntry={popoverEl => {
                  popoverEl?.scrollIntoView({ behavior: 'smooth' });
                }}
                currentStep="two"
                content={t(
                  'atlas.search.pagination.walk_through.show_results_per_page',
                  'Show 10, 25, or 50 search results per page.',
                  'Content for walkthrough popover.',
                )}
                title={t(
                  'atlas.search.pagination.walk_through.change_results_per_page',
                  'Change results per page',
                  'Title for walkthrough popover.',
                )}
                position={{ left: '140px' }}
                arrowDirection="topStart"
              />
            )}
          {tourCookieName === SEARCH_RESULTS_PAGINATION_TOUR_COOKIE &&
            currentStep === 3 && (
              <OnboardingPopover
                onComplete={() => endTour()}
                onNextClick={() => endTour()}
                onEntry={popoverEl => {
                  popoverEl?.scrollIntoView({ behavior: 'smooth' });
                }}
                currentStep="three"
                content={t(
                  'atlas.search.pagination.walk_through.paginate_to_another',
                  'Paginate from one page of results to another.',
                  'Content for walkthrough popover.',
                )}
                title={t(
                  'atlas.search.pagination.walk_through.browse_all_results',
                  'Browse all results',
                  'Title for walkthrough popover.',
                )}
                position={{ right: '14px' }}
                arrowDirection="topEnd"
              />
            )}
        </PaginationContainer>
      )}
    </>
  );
});
